import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/common/Header';
import Footer from '../components/common/Footer';
import MobileStickyBar from '../components/common/MobileStickyBar';
import { Check, ArrowRight, AlertTriangle, ChevronDown, Shield } from 'lucide-react';

const THEMES_ENFANT = [
  { nom: 'Princesse / Fées', emoji: '👸', ages: '3-8 ans', couleurs: ['#F4A7B2', '#FECDD3', '#FFF'] },
  { nom: 'Super-Héros', emoji: '🦸', ages: '4-10 ans', couleurs: ['#3B82F6', '#EF4444', '#FFDE3B'] },
  { nom: 'Dinosaures', emoji: '🦕', ages: '3-7 ans', couleurs: ['#22C55E', '#166534', '#FFF'] },
  { nom: 'Pirate', emoji: '🏴‍☠️', ages: '5-10 ans', couleurs: ['#0B1E20', '#D4AF37', '#FFF'] },
  { nom: 'Licorne', emoji: '🦄', ages: '4-9 ans', couleurs: ['#F4A7B2', '#C084FC', '#7DD3FC'] },
  { nom: 'Foot / Sport', emoji: '⚽', ages: '6-12 ans', couleurs: ['#22C55E', '#FFF', '#1F2937'] },
  { nom: 'Animaux de la forêt', emoji: '🦊', ages: '2-6 ans', couleurs: ['#92400E', '#D97706', '#FFF'] },
  { nom: 'Espace & Astronautes', emoji: '🚀', ages: '5-10 ans', couleurs: ['#0B1E20', '#8B5CF6', '#F59E0B'] },
];

const CHECKLIST_ENFANT = [
  { cat: 'Sécurité', items: ['Vérifier la certification des gonflables', 'S\'assurer de la surveillance adulte', 'Retirer les chaussures et ceintures', 'Éviter les bijoux pendant les jeux', 'Prévoir espace sécurisé pour les petits (< 3 ans)'] },
  { cat: 'Animations', items: ['Château gonflable adapté à l\'âge', 'Structures sécurisées et inspectées', 'Activités manuelles (coloriage, bricolage)', 'Chasse au trésor thématique', 'Animation musicale / karaoké kids'] },
  { cat: 'Gourmand', items: ['Barbe à papa', 'Popcorn', 'Bar à bonbons thématique', 'Gâteau d\'anniversaire', 'Boissons sans alcool uniquement'] },
  { cat: 'Déco & Accueil', items: ['Arche ballons couleur thème', 'Table décorée', 'Sachet souvenir pour chaque enfant', 'Nappe et vaisselle thématiques', 'Photo de groupe'] },
  { cat: 'Logistique', items: ['Invitations envoyées 3 semaines avant', 'Allergies collectées', 'Nombre final confirmé', 'Matériel Show Événement réservé', 'Responsable adulte par groupe de 5 enfants'] },
];

const TIMING = [
  ['14h00', 'Arrivée des invités, accueil', '#5DBCBF'],
  ['14h15', 'Installation & explication des jeux', '#D4AF37'],
  ['14h30', 'Château gonflable & animations libres', '#22C55E'],
  ['16h00', 'Goûter & gâteau d\'anniversaire', '#F4A7B2'],
  ['16h30', 'Soufflage bougies & chansons', '#F4A7B2'],
  ['17h00', 'Activité calme (bricolage, chasse trésor)', '#8B5CF6'],
  ['18h00', 'Sachet souvenir & fin de fête', '#D4AF37'],
];

export default function GuideAnniversaireEnfantPage() {
  const [checkItems, setCheckItems] = useState(
    CHECKLIST_ENFANT.flatMap(c => c.items.map(item => ({ label: item, cat: c.cat, done: false })))
  );
  const [selectedTheme, setSelectedTheme] = useState(null);
  const progress = Math.round(checkItems.filter(i => i.done).length / checkItems.length * 100);

  return (
    <div style={{ background: 'var(--bg)', minHeight: '100vh' }}>
      <Header />

      {/* HERO */}
      <section className="relative overflow-hidden" style={{ minHeight: '55vh' }}>
        <img src="https://images.unsplash.com/photo-1765947389633-461a4af7712b?w=1400&q=85"
          alt="Anniversaire Enfant" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(11,30,32,0.88) 0%, rgba(11,30,32,0.5) 60%, transparent 100%)' }} />
        <div className="relative container-ose flex flex-col justify-end pb-12" style={{ minHeight: '55vh', paddingTop: '5rem' }}>
          <nav className="flex items-center gap-2 text-xs mb-4" style={{ color: 'rgba(255,255,255,0.6)' }}>
            <Link to="/guides" style={{ color: '#5DBCBF' }}>Guides</Link>
            <span>/</span>
            <span style={{ color: '#FFF' }}>Anniversaire enfant</span>
          </nav>
          <span className="inline-block w-fit px-4 py-2 rounded-full text-xs font-bold mb-4"
            style={{ background: 'rgba(244,167,178,0.2)', color: '#F4A7B2', border: '1px solid rgba(244,167,178,0.4)' }}>
            🎈 Guide complet 2026 — 3 à 12 ans
          </span>
          <h1 className="font-display text-4xl sm:text-5xl font-bold mb-3"
            style={{ color: '#FFF', letterSpacing: '-0.03em', maxWidth: '700px', lineHeight: '1.05' }}>
            Organiser l'anniversaire parfait pour votre enfant
          </h1>
          <p className="text-sm mb-6 max-w-2xl" style={{ color: 'rgba(255,255,255,0.75)', lineHeight: '1.75' }}>
            Thèmes 2026, sécurité, checklist parents, timing et animations adaptées par âge.
          </p>
          <div className="flex flex-wrap gap-3">
            <a href="#checklist" className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-bold text-sm"
              style={{ background: '#F4A7B2', color: '#0B1E20' }}>
              <Check size={16} /> Checklist parents
            </a>
            <Link to="/creer-evenement" className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm"
              style={{ background: 'rgba(255,255,255,0.12)', color: '#FFF', backdropFilter: 'blur(8px)', border: '1px solid rgba(255,255,255,0.2)' }}>
              Créer mon devis <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* PROGRESSION */}
      <div style={{ background: '#0B1E20', borderBottom: '1px solid rgba(244,167,178,0.2)' }}>
        <div className="container-ose py-3 flex items-center gap-4">
          <p className="text-xs font-semibold" style={{ color: '#F4A7B2' }}>Préparation : {progress}%</p>
          <div className="flex-1 h-2 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.1)' }}>
            <div className="h-full rounded-full transition-all" style={{ width: `${progress}%`, background: '#F4A7B2' }} />
          </div>
        </div>
      </div>

      {/* THÈMES */}
      <section className="section-py section-white">
        <div className="container-ose">
          <div className="text-center mb-8">
            <h2 className="font-display text-2xl font-bold" style={{ color: 'var(--ink)' }}>8 thèmes tendance 2026</h2>
            <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>Cliquez pour explorer le thème</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {THEMES_ENFANT.map(t => (
              <button key={t.nom} onClick={() => setSelectedTheme(selectedTheme === t.nom ? null : t.nom)}
                className="p-4 rounded-2xl text-center transition-all"
                style={{ background: selectedTheme === t.nom ? '#0B1E20' : '#FFF', border: `1.5px solid ${selectedTheme === t.nom ? '#F4A7B2' : 'var(--border)'}`, cursor: 'pointer' }}>
                <div className="text-3xl mb-2">{t.emoji}</div>
                <p className="font-bold text-sm" style={{ color: selectedTheme === t.nom ? '#F4A7B2' : 'var(--ink)' }}>{t.nom}</p>
                <p className="text-xs" style={{ color: 'rgba(255,255,255,0.5)' }}>{t.ages}</p>
                {selectedTheme === t.nom && (
                  <div className="flex gap-1 justify-center mt-2">
                    {t.couleurs.map(c => <div key={c} className="w-4 h-4 rounded-full" style={{ background: c, border: '1px solid rgba(255,255,255,0.2)' }} />)}
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* SÉCURITÉ — très important pour les parents */}
      <section className="section-py" style={{ background: 'rgba(34,197,94,0.04)' }}>
        <div className="container-ose">
          <div className="flex items-center gap-3 mb-6">
            <Shield size={24} style={{ color: '#22C55E' }} />
            <h2 className="font-display text-2xl font-bold" style={{ color: 'var(--ink)' }}>Sécurité — Priorité absolue</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {[
              '✅ Tous nos gonflables sont certifiés CE et régulièrement inspectés',
              '✅ Retirer chaussures, ceintures et objets pointus avant de jouer',
              '✅ Maximum 1 adulte surveillant pour 5 enfants',
              '✅ Espace dédié aux moins de 3 ans — ne pas mélanger les âges',
              '✅ Déconseillé par vent > 50km/h — nous vous contacterons la veille',
              '✅ Soufflerie silencieuse incluse — fonctionne en continu pendant la fête',
            ].map((item, i) => (
              <div key={i} className="p-3 rounded-xl text-xs flex items-start gap-2"
                style={{ background: '#FFF', border: '1px solid rgba(34,197,94,0.2)' }}>
                <span style={{ color: 'var(--ink)', lineHeight: '1.6' }}>{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CHECKLIST */}
      <section id="checklist" className="section-py section-white">
        <div className="container-ose">
          <div className="text-center mb-8">
            <h2 className="font-display text-2xl font-bold" style={{ color: 'var(--ink)' }}>Checklist parents</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {CHECKLIST_ENFANT.map(cat => {
              const catItems = checkItems.filter(i => i.cat === cat.cat);
              const done = catItems.filter(i => i.done).length;
              return (
                <div key={cat.cat} className="card-premium p-5">
                  <div className="flex items-center justify-between mb-3">
                    <p className="font-bold text-sm" style={{ color: 'var(--ink)' }}>{cat.cat}</p>
                    <span className="text-xs font-bold" style={{ color: '#F4A7B2' }}>{done}/{catItems.length}</span>
                  </div>
                  {catItems.map(item => (
                    <button key={item.label} onClick={() => setCheckItems(prev => prev.map(i => i.label === item.label && i.cat === item.cat ? { ...i, done: !i.done } : i))}
                      className="w-full flex items-start gap-2 p-1.5 rounded-lg text-left mb-1"
                      style={{ background: item.done ? 'rgba(244,167,178,0.06)' : 'transparent', border: 'none', cursor: 'pointer' }}>
                      <div className="w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 mt-0.5"
                        style={{ borderColor: item.done ? '#F4A7B2' : 'var(--border)', background: item.done ? '#F4A7B2' : 'transparent' }}>
                        {item.done && <Check size={9} color="white" />}
                      </div>
                      <span className="text-xs" style={{ color: item.done ? 'var(--text-muted)' : 'var(--ink)', textDecoration: item.done ? 'line-through' : 'none' }}>{item.label}</span>
                    </button>
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* TIMING TYPE */}
      <section className="section-py" style={{ background: 'var(--bg)' }}>
        <div className="container-ose max-w-lg mx-auto">
          <div className="text-center mb-6">
            <h2 className="font-display text-xl font-bold" style={{ color: 'var(--ink)' }}>Timing type pour 15 enfants</h2>
          </div>
          {TIMING.map(([h, label, color], i) => (
            <div key={i} className="flex items-start gap-4 p-3 rounded-xl mb-2" style={{ background: '#FFF', border: '1px solid var(--border)' }}>
              <span className="font-bold text-sm w-14 flex-shrink-0" style={{ color }}>{h}</span>
              <p className="text-sm" style={{ color: 'var(--ink)' }}>{label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="section-py" style={{ background: '#0B1E20' }}>
        <div className="container-ose text-center">
          <h2 className="font-display text-2xl font-bold mb-4" style={{ color: '#FFF' }}>Toutes les animations pour votre enfant</h2>
          <p className="text-sm mb-8 max-w-lg mx-auto" style={{ color: 'rgba(255,255,255,0.6)' }}>
            Châteaux gonflables certifiés, barbe à papa, bar à bonbons, arche ballons — livraison et installation incluses.
          </p>
          <Link to="/creer-evenement"
            className="inline-flex items-center gap-2 px-8 py-4 rounded-xl font-bold"
            style={{ background: '#F4A7B2', color: '#0B1E20', boxShadow: '0 12px 40px rgba(244,167,178,0.3)' }}>
            Réserver les animations <ArrowRight size={18} />
          </Link>
        </div>
      </section>

      <Footer />
      <MobileStickyBar />
    </div>
  );
}
